import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

// Storage key matches the existing one written by the legacy DarkModeToggle
// so users keep their saved preference across this migration. The legacy
// code stored only "dark"/"light"; "system" is a new option.
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
  );

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    const resolved =
      theme === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : theme;

    root.classList.add(resolved);

    // Scrollbar tokens preserved from the legacy toggle so the custom
    // scrollbar styling in index.css keeps the same colors.
    if (resolved === "dark") {
      root.style.setProperty("--scrollbar-thumb", "#5A5A5A");
      root.style.setProperty("--scrollbar-track", "#1C1C1C");
    } else {
      root.style.setProperty("--scrollbar-thumb", "#9ca3af");
      root.style.setProperty("--scrollbar-track", "#e5e7eb");
    }

    // The legacy `themeChange` event is still listened to by a few
    // components (BarChartComponent reads `localStorage` and refreshes on
    // this event). Fire it for now so nothing breaks; we can rip out the
    // listeners once those components migrate to useTheme().
    window.dispatchEvent(new Event("themeChange"));
  }, [theme]);

  // Track OS preference changes when the user picked "system" so we flip
  // the class automatically without requiring a reload.
  useEffect(() => {
    if (theme !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(mql.matches ? "dark" : "light");
      window.dispatchEvent(new Event("themeChange"));
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  const value = {
    theme,
    setTheme: (next: Theme) => {
      localStorage.setItem(storageKey, next);
      setTheme(next);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
